import {
  IKazokugotoGenerationLogResponse,
  IKazokugotoWebSearchLog,
} from '@/types/response';
import { Space, Spin, Tag, Typography } from 'antd';

const { Text } = Typography;

const SEARCH_LABEL: Record<string, string> = {
  themeWebSearch: 'テーマ選定',
  factCheckWebSearch: 'ファクトチェック',
};

// URL は AI の web 検索結果（外部由来データ）なので、javascript: 等の
// 危険なスキームを弾き http(s) のみリンク化する
const isSafeHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const LinkList: React.FC<{
  links: { url: string; title?: string; citedText?: string }[];
}> = ({ links }) => (
  <ul style={{ margin: 0, paddingLeft: 20 }}>
    {links.map((link) => (
      <li key={link.url} style={{ fontSize: 12, marginBottom: 2 }}>
        {isSafeHttpUrl(link.url) ? (
          <a href={link.url} target='_blank' rel='noopener noreferrer'>
            {link.title || link.url}
          </a>
        ) : (
          <Text style={{ fontSize: 12 }}>{link.title || link.url}</Text>
        )}
        {link.citedText && (
          <Text type='secondary' style={{ fontSize: 11, display: 'block' }}>
            “{link.citedText}”
          </Text>
        )}
      </li>
    ))}
  </ul>
);

const WebSearchLogBlock: React.FC<{
  label: string;
  log: IKazokugotoWebSearchLog;
}> = ({ label, log }) => {
  const empty =
    log.queries.length === 0 &&
    log.results.length === 0 &&
    log.citations.length === 0;

  return (
    <div
      style={{
        background: '#fafafa',
        padding: 12,
        borderRadius: 4,
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {label}
      </Text>
      {empty ? (
        <Text type='secondary' style={{ fontSize: 12 }}>
          web 検索は実行されていません
        </Text>
      ) : (
        <Space direction='vertical' size={8} style={{ width: '100%' }}>
          {log.queries.length > 0 && (
            <div>
              <Text type='secondary' style={{ fontSize: 12 }}>
                検索クエリ:
              </Text>{' '}
              {log.queries.map((q, i) => (
                <Tag key={`${q}-${i}`}>{q}</Tag>
              ))}
            </div>
          )}
          {log.citations.length > 0 && (
            <div>
              <Text type='secondary' style={{ fontSize: 12, display: 'block' }}>
                引用リンク（本文が参照）:
              </Text>
              <LinkList links={log.citations} />
            </div>
          )}
          {log.results.length > 0 && (
            <div>
              <Text type='secondary' style={{ fontSize: 12, display: 'block' }}>
                検索結果リンク:
              </Text>
              <LinkList links={log.results} />
            </div>
          )}
        </Space>
      )}
    </div>
  );
};

/**
 * 生成時の web 検索ログ（参照リンク）表示セクション。
 * ログ未保存の item（機能追加前の生成分など）は「ログなし」表示。
 */
export const GenerationLogSection: React.FC<{
  loading: boolean;
  data?: IKazokugotoGenerationLogResponse;
}> = ({ loading, data }) => {
  const log = data?.generationLog;
  const entries = log
    ? Object.entries(log).filter(([, v]) => v !== null && v !== undefined)
    : [];

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 4 }}>
        生成ログ（web 検索の参照リンク）
      </Text>
      {loading ? (
        <Spin size='small' />
      ) : entries.length === 0 ? (
        <Text type='secondary' style={{ fontSize: 12 }}>
          ログなし（この item の生成時にはログ保存機能がありませんでした）
        </Text>
      ) : (
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          {entries.map(([key, value]) => (
            <WebSearchLogBlock
              key={key}
              label={SEARCH_LABEL[key] ?? key}
              log={value as IKazokugotoWebSearchLog}
            />
          ))}
        </Space>
      )}
    </div>
  );
};
